import React, { useState } from 'react';
import { TableCell, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Download, Archive, FileText, Image, FileSpreadsheet, Eye, Pencil, History } from 'lucide-react';
import { getDownloadUrl } from './participantDocumentsApi';
import { ParticipantDocumentPreviewModal } from './ParticipantDocumentPreviewModal';
import { DocumentVersionHistoryModal } from './DocumentVersionHistoryModal';
import { DocumentEditModal } from './DocumentEditModal';
import api from '@/lib/api';
import { toast } from 'sonner';

const CATEGORY_LABELS = {
  ID: 'ID',
  SERVICE_AGREEMENT: 'Service Agreement',
  CONSENT: 'Consent',
  POLICY: 'Policy',
  HR: 'HR',
  INCIDENT_DOC: 'Incident',
  OTHER: 'Other',
};

function getFileIcon(mimeType) {
  if (!mimeType) return FileText;
  if (mimeType.startsWith('image/')) return Image;
  if (mimeType.includes('spreadsheet') || mimeType.includes('csv')) return FileSpreadsheet;
  return FileText;
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
}

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function getExpiryBadge(expiryDate) {
  if (!expiryDate) return null;
  const now = new Date();
  const expiry = new Date(expiryDate);
  const diffDays = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return <Badge variant="destructive" className="text-[10px] font-normal" data-testid="expiry-badge-expired">Expired</Badge>;
  }
  if (diffDays <= 30) {
    return <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] font-normal" data-testid="expiry-badge-expiring">Expiring Soon</Badge>;
  }
  return <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] font-normal" data-testid="expiry-badge-valid">Valid</Badge>;
}

export function ParticipantDocumentRow({ doc, participantId, canManage, canDownload, canPreview, onArchive, onEdited }) {
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const Icon = getFileIcon(doc.mimeType);

  // Use per-document API flags when available, fall back to role-level props
  const docCanPreview = doc.can_preview !== undefined ? doc.can_preview : canPreview;
  const docCanDownload = doc.can_download !== undefined ? doc.can_download : canDownload;
  const docCanEdit = doc.can_edit !== undefined ? doc.can_edit : canManage;
  const docCanArchive = doc.can_archive !== undefined ? doc.can_archive : canManage;

  const handleDownload = async () => {
    try {
      const url = getDownloadUrl(participantId, doc.id);
      const res = await api.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = doc.fileName || doc.title || 'document';
      link.click();
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    } catch {
      toast.error('Failed to download document');
    }
  };

  const handleArchive = async () => {
    setArchiving(true);
    try {
      await onArchive(doc.id);
      setArchiveOpen(false);
    } finally {
      setArchiving(false);
    }
  };

  return (
    <>
      <TableRow data-testid={`doc-row-${doc.id}`}>
        {/* Document Name + meta */}
        <TableCell>
          <div className="flex items-start gap-2.5">
            <div className="mt-0.5 p-1.5 rounded bg-muted/50">
              <Icon className="w-4 h-4 text-muted-foreground" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="text-sm font-medium truncate max-w-[240px]" title={doc.title}>{doc.title}</p>
                {(doc.version && doc.version > 1) && (
                  <Badge variant="secondary" className="text-[9px] px-1 py-0 flex-shrink-0" data-testid={`doc-version-badge-${doc.id}`}>v{doc.version}</Badge>
                )}
                {doc.isLatest === false && (
                  <Badge variant="outline" className="text-[9px] px-1 py-0 text-muted-foreground flex-shrink-0">old</Badge>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground">
                {doc.fileName && <span className="truncate max-w-[180px] inline-block align-bottom">{doc.fileName}</span>}
                {doc.fileSize ? <span className="ml-1.5">({formatFileSize(doc.fileSize)})</span> : null}
              </p>
            </div>
          </div>
        </TableCell>

        {/* Type */}
        <TableCell>
          <Badge variant="outline" className="text-[10px] font-normal">
            {CATEGORY_LABELS[doc.category] || doc.category}
          </Badge>
        </TableCell>

        {/* Uploaded Date */}
        <TableCell className="text-xs text-muted-foreground">{formatDate(doc.createdAt)}</TableCell>

        {/* Expiry */}
        <TableCell>
          {doc.expiryDate ? (
            <div className="space-y-0.5">
              <span className="text-xs text-muted-foreground">{formatDate(doc.expiryDate)}</span>
              {getExpiryBadge(doc.expiryDate)}
            </div>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </TableCell>

        {/* Uploaded By + Role + Context */}
        <TableCell>
          <div className="space-y-0.5">
            <span className="text-xs text-muted-foreground truncate block max-w-[140px]">
              {doc.uploadedByName || '—'}
            </span>
            <div className="flex items-center gap-1 flex-wrap">
              {doc.uploadedByRole && (
                <Badge variant="secondary" className="text-[9px] font-normal px-1 py-0">{doc.uploadedByRole}</Badge>
              )}
              {doc.sourceContext && doc.sourceContext !== 'manual' && (
                <Badge className="bg-indigo-50 text-indigo-600 border-indigo-200 text-[9px] font-normal px-1 py-0" data-testid={`doc-context-${doc.id}`}>
                  {doc.sourceContext}
                </Badge>
              )}
            </div>
          </div>
        </TableCell>

        {/* Actions */}
        <TableCell className="text-right">
          <div className="flex items-center justify-end gap-1">
            {/* View / Preview */}
            {docCanPreview && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => setPreviewOpen(true)}
                title="View"
                data-testid={`doc-view-${doc.id}`}
              >
                <Eye className="w-3.5 h-3.5" />
              </Button>
            )}
            {/* Download — per-document flag controls visibility */}
            {docCanDownload && (
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handleDownload} title="Download" data-testid={`doc-download-${doc.id}`}>
                <Download className="w-3.5 h-3.5" />
              </Button>
            )}
            {/* Edit — only for manage perm */}
            {docCanEdit && (
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setEditOpen(true)} title="Edit" data-testid={`doc-edit-${doc.id}`}>
                <Pencil className="w-3.5 h-3.5" />
              </Button>
            )}
            {/* Version History — show if version > 1 or has previousVersionId */}
            {docCanEdit && (doc.version > 1 || doc.previousVersionId) && (
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setVersionsOpen(true)} title="Version History" data-testid={`doc-versions-${doc.id}`}>
                <History className="w-3.5 h-3.5" />
              </Button>
            )}
            {/* Archive — only for manage perm */}
            {docCanArchive && (
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:text-destructive" onClick={() => setArchiveOpen(true)} title="Archive" data-testid={`doc-archive-${doc.id}`}>
                <Archive className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        </TableCell>
      </TableRow>

      {/* Preview Modal */}
      {previewOpen && (
        <ParticipantDocumentPreviewModal
          doc={doc}
          participantId={participantId}
          canDownload={docCanDownload}
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
        />
      )}

      {/* Archive Confirmation Dialog */}
      <Dialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Archive Document</DialogTitle>
            <DialogDescription>
              Are you sure you want to archive &ldquo;{doc.title}&rdquo;? This will hide it from the active list.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setArchiveOpen(false)}>Cancel</Button>
            <Button variant="destructive" size="sm" onClick={handleArchive} disabled={archiving} data-testid="confirm-archive-btn">
              {archiving ? 'Archiving...' : 'Archive'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Version History Modal */}
      {versionsOpen && (
        <DocumentVersionHistoryModal doc={doc} participantId={participantId} open={versionsOpen} onClose={() => setVersionsOpen(false)} />
      )}

      {/* Edit Metadata Modal */}
      {editOpen && (
        <DocumentEditModal doc={doc} participantId={participantId} open={editOpen} onClose={() => setEditOpen(false)} onComplete={() => { setEditOpen(false); if (onEdited) onEdited(); }} />
      )}
    </>
  );
}
